import React, { useCallback, useMemo, useState } from 'react';

import { CustomOperatorValue } from '@aline/common';
import {
  Autocomplete,
  capitalize,
  FormControl,
  IconButton,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import type {
  GridApi,
  GridColDef,
  GridFilterItem,
  GridTranslationKeys,
} from '@mui/x-data-grid-premium';
import { GridLogicOperator } from '@mui/x-data-grid-premium';
import { matchSorter } from 'match-sorter';
import { ReportFilterValueInputAdapter } from 'src/components';
import { TrashcanIcon } from 'src/components/icons/TrashcanIcon';
import { makeStyles } from 'tss-react/mui';

import type { ReportFilterPanelLocaleText } from './report-filters-panel-core';

// Operators that don't require input values (they check for empty/non-empty state)
const NO_INPUT_OPERATORS = new Set([
  'isEmpty',
  'isNotEmpty',
  CustomOperatorValue.BooleanIsEmpty,
  CustomOperatorValue.BooleanIsNotEmpty,
  CustomOperatorValue.ContractTypeIsEmpty,
  CustomOperatorValue.ContractTypeIsNotEmpty,
  CustomOperatorValue.PartyIsEmpty,
  CustomOperatorValue.PartyIsNotEmpty,
  CustomOperatorValue.LabelIsEmpty,
  CustomOperatorValue.LabelIsNotEmpty,
  CustomOperatorValue.WorkflowAssigneeIsEmpty,
  CustomOperatorValue.WorkflowAssigneeIsNotEmpty,
]);

/**
 * A compact row sits in the AI report approval card's popover, which sizes
 * itself to this row rather than to the grid's filter panel. Each control
 * takes a fixed width so the row has a predictable total, and the controls
 * inside it are held to that width: the value input's autocomplete asks for
 * 500px on its own, which is what blew the row past the popover.
 */
function createCompactField(width: number) {
  return {
    '& .MuiAutocomplete-root, & .MuiInputBase-root, & select': {
      maxWidth: '100%',
      width: '100%',
    },
    '& select': { textOverflow: 'ellipsis' },
    minWidth: 0,
    width,
  };
}

const useStyles = makeStyles<{ compact: boolean }>({ name: 'MuiGridFilterForm' })(
  (theme, { compact }) => ({
    closeIcon: { flexShrink: 0, justifyContent: 'flex-end', marginBottom: 2, marginRight: 6 },
    columnSelect: compact
      ? { ...createCompactField(180), marginRight: theme.spacing(1) }
      : { marginRight: theme.spacing(2), minWidth: 190 },
    container: { alignItems: 'center', display: 'flex', minWidth: 0 },
    filterValueInput: compact
      ? { ...createCompactField(240), flexGrow: 1, marginRight: theme.spacing(1) }
      : { flexGrow: 1, marginRight: theme.spacing(2), minWidth: 190 },
    linkOperatorSelect: compact
      ? { ...createCompactField(64), marginRight: theme.spacing(1) }
      : { marginRight: theme.spacing(1), minWidth: 60 },
    operatorSelect: compact
      ? { ...createCompactField(132), marginRight: theme.spacing(1) }
      : { marginRight: theme.spacing(2), minWidth: 120 },
  })
);

interface GridFilterFormProps {
  applyFilterChanges: (item: GridFilterItem) => void;
  applyMultiFilterOperatorChanges: (operator: GridLogicOperator) => void;
  columns: readonly GridColDef[];
  deleteFilter: (item: GridFilterItem) => void;
  disableMultiFilterOperator: boolean;
  hasMultipleFilters: boolean;
  item: GridFilterItem;
  localeText: ReportFilterPanelLocaleText;
  showMultiFilterOperators: boolean;
  apiRef?: React.RefObject<GridApi | null>;
  compact?: boolean;
  multiFilterOperator?: GridLogicOperator;
}

export function ReportFiltersPanelRow({
  apiRef,
  applyFilterChanges,
  applyMultiFilterOperatorChanges,
  columns,
  compact = false,
  deleteFilter,
  disableMultiFilterOperator,
  hasMultipleFilters,
  item,
  localeText,
  multiFilterOperator,
  showMultiFilterOperators,
}: GridFilterFormProps): JSX.Element {
  const { classes } = useStyles({ compact });
  const [inputValue, setInputValue] = useState<string>('');
  const [columnSelectOpen, setColumnSelectOpen] = useState(false);

  const options = useMemo(
    () => columns.slice().sort((a, b) => (a.headerName ?? '').localeCompare(b.headerName ?? '')),
    [columns]
  );

  const getOptionLabel = (colDef: GridColDef): string => {
    return colDef?.headerName ?? '';
  };

  const handleInputChange = (
    _event: React.SyntheticEvent,
    newInputValue: string,
    reason: string
  ): void => {
    if (reason === 'reset' && columnSelectOpen) return;
    setInputValue(newInputValue);
  };

  const filterOptions = (opts: GridColDef[]): GridColDef[] => {
    if (!inputValue) return opts;
    return matchSorter(opts, inputValue, {
      keys: ['headerName' satisfies keyof GridColDef],
    });
  };

  const getCurrentColumn = useCallback(() => {
    if (!item.field) return null;
    return columns.find(column => column.field === item.field) ?? null;
  }, [columns, item.field]);

  const getCurrentOperator = useCallback(() => {
    const currentColumn = getCurrentColumn();
    if (!currentColumn) return null;

    const currentValue = item.operator ?? currentColumn.filterOperators?.[0]?.value;
    return currentColumn.filterOperators?.find(op => op.value === currentValue) ?? null;
  }, [item.operator, getCurrentColumn]);

  const changeColumn = useCallback(
    (colDef: GridColDef | null) => {
      const firstOp = colDef?.filterOperators?.at(0);
      if (!firstOp || !colDef) return;

      applyFilterChanges({
        ...item,
        field: colDef.field,
        operator: firstOp.value,
        value: undefined,
      });
    },
    [applyFilterChanges, item]
  );

  const changeOperator = useCallback(
    (event: SelectChangeEvent) => {
      const next = typeof event.target.value === 'string' ? event.target.value : '';
      const currentColumn = getCurrentColumn();
      const currentOperator = getCurrentOperator();
      const nextOperator = currentColumn?.filterOperators?.find(
        operator => operator.value === next
      );
      const preserveValue = nextOperator?.InputComponent === currentOperator?.InputComponent;
      applyFilterChanges({
        ...item,
        operator: next,
        value: preserveValue ? item.value : undefined,
      });
    },
    [applyFilterChanges, getCurrentColumn, getCurrentOperator, item]
  );

  const changeLinkOperator = useCallback(
    (event: SelectChangeEvent) => {
      if (typeof event.target.value !== 'string') return;
      const linkOperator =
        event.target.value === GridLogicOperator.And.toString()
          ? GridLogicOperator.And
          : GridLogicOperator.Or;
      applyMultiFilterOperatorChanges(linkOperator);
    },
    [applyMultiFilterOperatorChanges]
  );

  const handleDeleteFilter = useCallback(() => {
    deleteFilter(item);
  }, [deleteFilter, item]);

  const currentOperator = getCurrentOperator();
  const operatorRequiresInput = !NO_INPUT_OPERATORS.has(currentOperator?.value ?? '');

  return (
    <div className={classes.container} onKeyDown={e => e.stopPropagation()}>
      <FormControl
        className={classes.linkOperatorSelect}
        style={{
          display: hasMultipleFilters ? 'block' : 'none',
          visibility: showMultiFilterOperators ? 'visible' : 'hidden',
        }}
        variant="standard"
      >
        <Select
          native
          disabled={disableMultiFilterOperator}
          value={multiFilterOperator ?? GridLogicOperator.And}
          onChange={changeLinkOperator}
        >
          <option value={GridLogicOperator.And}>{localeText.and}</option>
          <option value={GridLogicOperator.Or}>{localeText.or}</option>
        </Select>
      </FormControl>
      <FormControl className={classes.columnSelect} variant="standard">
        <Autocomplete<GridColDef, false, false, false>
          blurOnSelect
          openOnFocus
          filterOptions={filterOptions}
          getOptionKey={option => option.field}
          getOptionLabel={getOptionLabel}
          inputValue={inputValue}
          open={columnSelectOpen}
          options={options}
          renderInput={params => <TextField {...params} variant="standard" />}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            const text = getOptionLabel(option);
            return (
              <Typography key={key} {...rest} noWrap>
                {text}
              </Typography>
            );
          }}
          slotProps={{
            popper: {
              modifiers: [
                {
                  enabled: false,
                  name: 'flip',
                },
                {
                  enabled: false,
                  name: 'preventOverflow',
                },
              ],
            },
          }}
          value={getCurrentColumn()}
          onChange={(_event, colDef) => changeColumn(colDef)}
          onClose={() => setColumnSelectOpen(false)}
          onInputChange={handleInputChange}
          onOpen={() => {
            setColumnSelectOpen(true);
            setInputValue('');
          }}
        />
      </FormControl>
      <FormControl className={classes.operatorSelect} variant="standard">
        <Select native value={item.operator} onChange={changeOperator}>
          {getCurrentColumn()?.filterOperators?.map(operator => {
            const maybeTranslationKey = `filterOperator${capitalize(operator.value)}`;

            if (!getIsGridTranslationKey(maybeTranslationKey))
              return (
                <option key={operator.value} value={operator.value}>
                  {String(operator.label) || 'unknown operator'}
                </option>
              );

            const label = localeText.getOperatorLabel(maybeTranslationKey);
            if (typeof label !== 'string')
              return (
                <option key={operator.value} value={operator.value}>
                  {String(operator.label) || 'unknown operator'}
                </option>
              );

            return (
              <option key={operator.value} value={operator.value}>
                {label}
              </option>
            );
          })}
        </Select>
      </FormControl>
      <FormControl
        className={classes.filterValueInput}
        style={{ visibility: operatorRequiresInput ? 'visible' : 'hidden' }}
        variant="standard"
      >
        {operatorRequiresInput && (
          <ReportFilterValueInputAdapter
            apiRef={apiRef}
            applyValue={applyFilterChanges}
            column={getCurrentColumn()}
            item={item}
            operator={currentOperator}
          />
        )}
      </FormControl>
      <FormControl className={classes.closeIcon} variant="standard">
        <IconButton
          aria-label={localeText.deleteFilter}
          size="small"
          title={localeText.deleteFilter}
          onClick={handleDeleteFilter}
        >
          <TrashcanIcon />
        </IconButton>
      </FormControl>
    </div>
  );
}

const gridLocaleRecord: Record<GridTranslationKeys, ''> = {
  actionsCellMore: '',
  aggregationFunctionLabelAvg: '',
  aggregationFunctionLabelMax: '',
  aggregationFunctionLabelMin: '',
  aggregationFunctionLabelNone: '',
  aggregationFunctionLabelSize: '',
  aggregationFunctionLabelSum: '',
  aggregationMenuItemHeader: '',
  aiAssistantPanelClose: '',
  aiAssistantPanelConversationHistory: '',
  aiAssistantPanelEmptyConversation: '',
  aiAssistantPanelNewConversation: '',
  aiAssistantPanelTitle: '',
  aiAssistantSuggestions: '',
  booleanCellFalseLabel: '',
  booleanCellTrueLabel: '',
  chartsCategories: '',
  chartsChartNotSelected: '',
  chartsCloseButton: '',
  chartsDragToDimensions: '',
  chartsDragToValues: '',
  chartsFieldBlocked: '',
  chartsMenuAddToDimensions: '',
  chartsMenuAddToValues: '',
  chartsMenuMoveDown: '',
  chartsMenuMoveToBottom: '',
  chartsMenuMoveToTop: '',
  chartsMenuMoveUp: '',
  chartsMenuOptions: '',
  chartsMenuRemove: '',
  chartsNoCharts: '',
  chartsNoFields: '',
  chartsSearchClear: '',
  chartsSearchLabel: '',
  chartsSearchPlaceholder: '',
  chartsSeries: '',
  chartsSyncButtonLabel: '',
  chartsTabChart: '',
  chartsTabCustomize: '',
  chartsTabFields: '',
  checkboxSelectionHeaderName: '',
  checkboxSelectionSelectAllRows: '',
  checkboxSelectionSelectRow: '',
  checkboxSelectionUnselectAllRows: '',
  checkboxSelectionUnselectRow: '',
  collapseDetailPanel: '',
  columnHeaderFiltersLabel: '',
  columnHeaderFiltersTooltipActive: '',
  columnHeaderSortIconLabel: '',
  columnMenuAriaLabel: '',
  columnMenuFilter: '',
  columnMenuHideColumn: '',
  columnMenuLabel: '',
  columnMenuManageCharts: '',
  columnMenuManageColumns: '',
  columnMenuManagePivot: '',
  columnMenuShowColumns: '',
  columnMenuSortAsc: '',
  columnMenuSortDesc: '',
  columnMenuUnsort: '',
  columnsManagementDeleteIconLabel: '',
  columnsManagementNoColumns: '',
  columnsManagementReset: '',
  columnsManagementSearchTitle: '',
  columnsManagementShowHideAllText: '',
  detailPanelToggle: '',
  emptyPivotOverlayLabel: '',
  expandDetailPanel: '',
  'filterOperator!=': '',
  'filterOperator<': '',
  'filterOperator<=': '',
  'filterOperator=': '',
  'filterOperator>': '',
  'filterOperator>=': '',
  filterOperatorAfter: '',
  filterOperatorBefore: '',
  filterOperatorContains: '',
  filterOperatorDoesNotContain: '',
  filterOperatorDoesNotEqual: '',
  filterOperatorEndsWith: '',
  filterOperatorEquals: '',
  filterOperatorIs: '',
  filterOperatorIsAnyOf: '',
  filterOperatorIsEmpty: '',
  filterOperatorIsNotEmpty: '',
  filterOperatorNot: '',
  filterOperatorOnOrAfter: '',
  filterOperatorOnOrBefore: '',
  filterOperatorStartsWith: '',
  filterPanelAddFilter: '',
  filterPanelColumn: '',
  filterPanelDeleteIconLabel: '',
  filterPanelInputLabel: '',
  filterPanelInputPlaceholder: '',
  filterPanelLogicOperator: '',
  filterPanelOperator: '',
  filterPanelOperatorAnd: '',
  filterPanelOperatorOr: '',
  filterPanelRemoveAll: '',
  filterValueAny: '',
  filterValueFalse: '',
  filterValueTrue: '',
  footerRowSelected: '',
  footerTotalRows: '',
  footerTotalVisibleRows: '',
  groupColumn: '',
  groupingColumnHeaderName: '',
  headerFilterClear: '',
  'headerFilterOperator!=': '',
  'headerFilterOperator<': '',
  'headerFilterOperator<=': '',
  'headerFilterOperator=': '',
  'headerFilterOperator>': '',
  'headerFilterOperator>=': '',
  headerFilterOperatorAfter: '',
  headerFilterOperatorBefore: '',
  headerFilterOperatorContains: '',
  headerFilterOperatorDoesNotContain: '',
  headerFilterOperatorDoesNotEqual: '',
  headerFilterOperatorEndsWith: '',
  headerFilterOperatorEquals: '',
  headerFilterOperatorIs: '',
  headerFilterOperatorIsAnyOf: '',
  headerFilterOperatorIsEmpty: '',
  headerFilterOperatorIsNotEmpty: '',
  headerFilterOperatorNot: '',
  headerFilterOperatorOnOrAfter: '',
  headerFilterOperatorOnOrBefore: '',
  headerFilterOperatorStartsWith: '',
  longTextCellCollapseLabel: '',
  longTextCellExpandLabel: '',
  noColumnsOverlayLabel: '',
  noColumnsOverlayManageColumns: '',
  noResultsOverlayLabel: '',
  noRowsLabel: '',
  paginationDisplayedRows: '',
  paginationItemAriaLabel: '',
  paginationRowsPerPage: '',
  pinToLeft: '',
  pinToRight: '',
  pivotCloseButton: '',
  pivotColumns: '',
  pivotDragToColumns: '',
  pivotDragToRows: '',
  pivotDragToValues: '',
  pivotMenuAddToColumns: '',
  pivotMenuAddToRows: '',
  pivotMenuAddToValues: '',
  pivotMenuColumns: '',
  pivotMenuMoveDown: '',
  pivotMenuMoveToBottom: '',
  pivotMenuMoveToTop: '',
  pivotMenuMoveUp: '',
  pivotMenuOptions: '',
  pivotMenuRemove: '',
  pivotMenuRows: '',
  pivotMenuValues: '',
  pivotNoFields: '',
  pivotQuarterColumnHeaderName: '',
  pivotRows: '',
  pivotSearchButton: '',
  pivotSearchControlClear: '',
  pivotSearchControlLabel: '',
  pivotSearchControlPlaceholder: '',
  pivotToggleLabel: '',
  pivotValues: '',
  pivotYearColumnHeaderName: '',
  promptAppliedChanges: '',
  promptChangeAggregationDescription: '',
  promptChangeAggregationLabel: '',
  promptChangeChartsLabel: '',
  promptChangeFilterDescription: '',
  promptChangeFilterLabel: '',
  promptChangeGroupDescription: '',
  promptChangePivotColumnsDescription: '',
  promptChangePivotColumnsLabel: '',
  promptChangePivotEnableDescription: '',
  promptChangePivotEnableLabel: '',
  promptChangePivotRowsLabel: '',
  promptChangePivotValuesDescription: '',
  promptChangePivotValuesLabel: '',
  promptChangeSortDescription: '',
  promptFieldLabel: '',
  promptFieldPlaceholder: '',
  promptFieldPlaceholderListening: '',
  promptFieldPlaceholderWithRecording: '',
  promptFieldRecord: '',
  promptFieldSend: '',
  promptFieldSpeechRecognitionNotSupported: '',
  promptFieldStopRecording: '',
  promptProcessing: '',
  promptRerun: '',
  rowReorderingHeaderName: '',
  toolbarAssistant: '',
  toolbarCharts: '',
  toolbarColumns: '',
  toolbarColumnsLabel: '',
  toolbarDensity: '',
  toolbarDensityComfortable: '',
  toolbarDensityCompact: '',
  toolbarDensityLabel: '',
  toolbarDensityStandard: '',
  toolbarExport: '',
  toolbarExportCSV: '',
  toolbarExportExcel: '',
  toolbarExportLabel: '',
  toolbarExportPrint: '',
  toolbarFilters: '',
  toolbarFiltersLabel: '',
  toolbarFiltersTooltipActive: '',
  toolbarFiltersTooltipHide: '',
  toolbarFiltersTooltipShow: '',
  toolbarPivot: '',
  toolbarQuickFilterDeleteIconLabel: '',
  toolbarQuickFilterLabel: '',
  toolbarQuickFilterPlaceholder: '',
  toolbarRedo: '',
  toolbarUndo: '',
  treeDataCollapse: '',
  treeDataExpand: '',
  treeDataGroupingHeaderName: '',
  unGroupColumn: '',
  unpin: '',
};

export function getIsGridTranslationKey(key: string): key is GridTranslationKeys {
  return key in gridLocaleRecord;
}
